import { LitElement } from "lit"
import { customElement, property } from "lit/decorators.js"
import mapboxgl from "mapbox-gl"
import type { MapControl, MapControlPosition } from "../map-control.ts"
import { MAP_EVENT, mapEvent, type MapTripSummary, parseMapTripSummaries } from "@/lib/map/types.ts"

/**
 * TripNavigatorControl — renders Prev/Next buttons and the current trip title
 * inside a globe-view. Dispatches `trip-selected` CustomEvent on the host
 * element with `detail: { tripId }`. Does not navigate itself.
 */
@customElement("trip-navigator-control")
export class TripNavigatorControl extends LitElement implements MapControl {
  override createRenderRoot() {
    return this
  }

  @property({ attribute: "position" })
  accessor position: MapControlPosition = "bottom-right"

  /** JSON array of { id, title } objects matching the globe's trips. */
  @property({ attribute: "trips" })
  accessor tripsJson: string = "[]"

  private _map: mapboxgl.Map | null = null
  private _container: HTMLElement | null = null
  private _currentIdx = 0
  private _trips: MapTripSummary[] = []

  onAdd(map: mapboxgl.Map): HTMLElement {
    this._map = map
    this._trips = this._parseTrips()
    this._container = document.createElement("div")
    this._container.className = "mapboxgl-ctrl mapboxgl-ctrl-group"
    this._container.style.cssText = "display: flex; align-items: center; min-width: 160px;"
    this._render()
    return this._container
  }

  onRemove(_map: mapboxgl.Map): void {
    this._container?.remove()
    this._container = null
    this._map = null
  }

  private _parseTrips(): MapTripSummary[] {
    return parseMapTripSummaries(this.tripsJson)
  }

  private _render() {
    if (!this._container) return
    this._container.innerHTML = ""

    if (this._trips.length === 0) {
      const empty = document.createElement("div")
      empty.style.cssText = "padding: 6px 10px; font-size: 12px; color: #9ca3af;"
      empty.textContent = "No trips"
      this._container.appendChild(empty)
      return
    }

    const makeBtn = (label: string, onClick: () => void, disabled = false) => {
      const btn = document.createElement("button")
      btn.type = "button"
      btn.textContent = label
      btn.disabled = disabled
      btn.style.cssText =
        "padding: 6px 8px; font-size: 13px; border: none; background: #fff; color: #374151; cursor: pointer; flex-shrink: 0;"
      if (disabled) btn.style.opacity = "0.4"
      btn.addEventListener("click", onClick)
      return btn
    }

    this._container.appendChild(
      makeBtn("‹", () => this._navigate(-1), this._currentIdx === 0),
    )

    const title = document.createElement("div")
    title.style.cssText =
      "flex: 1; padding: 0 8px; font-size: 12px; font-weight: 500; text-align: center; color: #111827; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 140px;"
    title.textContent = this._trips[this._currentIdx]?.title ?? ""
    this._container.appendChild(title)

    this._container.appendChild(
      makeBtn(
        "›",
        () => this._navigate(1),
        this._currentIdx === this._trips.length - 1,
      ),
    )
  }

  private _navigate(delta: number) {
    const next = this._currentIdx + delta
    if (next < 0 || next >= this._trips.length) return
    this._currentIdx = next
    this._render()

    const trip = this._trips[this._currentIdx]!
    const mapEl = this.closest("trip-map, globe-view")
    mapEl?.dispatchEvent(mapEvent(MAP_EVENT.tripSelected, { tripId: trip.id }))
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "trip-navigator-control": TripNavigatorControl
  }
}
